import React, { useEffect, useState } from "react"
import { AdminTable } from "../AdminTable"
import { useDispatch, useSelector } from "react-redux"
import { createCampaign, deactivateCampaign, getCampaigns, getCampaignTypes, updateCampaign } from "../../../redux/campaigns/thunks";
import { selectCampaigns, selectCampaignsLoading, selectCampaignTypes, selectCampaignTypesLoading } from "../../../redux/campaigns/selectors";
import { Button, Column } from "devextreme-react/data-grid";
import { selectTaxonomyDescriptions, selectTaxonomyDescriptionsLoading } from "../../../redux/taxonomyDescriptions/selectors";
import { getTaxonomyDescriptions } from "../../../redux/taxonomyDescriptions/thunks";
import { CampaignsDialogue } from "./CampaignsDialogue";
import { generate, generateMultiple } from "generate-password-browser";
import { openModal } from "../../../redux/modal/modalSlice";
import { DateBox } from "devextreme-react";
import { convertToUTC } from "../../../commonFunctions/commonFunctions";
import TreeSelector from "../../tableform/TreeSelector";

const initialDialogData = {
    campaignName: "",
    startDate: null,
    endDate: null,
    active: false,
    taxonomies: [
       {
           level_1: null,
           level_2: null,
           level_3: null,
           level_4: null,
           externalId: generate({length: 8})
       }
    ],
    rejectTaxonomy:{
        level_1: null,
        level_2: null,
        level_3: null,
        level_4: null
    }
}

const initialFilters={
        startDate: null,
        endDate: null   
}

const initialSortOptions = {
    columna: "startDate",
    sortOrderAsc: false

}

function CampaignManagement(props)
{

    const dispatch = useDispatch();
    const campaigns = useSelector(selectCampaigns);
    const taxonomyDescriptions = useSelector(selectTaxonomyDescriptions)
    const campaignTypes = useSelector(selectCampaignTypes);
    const loadingTaxonomies = useSelector(selectTaxonomyDescriptionsLoading)
    const loadingCampaigns = useSelector(selectCampaignsLoading);
    const loadingCampaignTypes = useSelector(selectCampaignTypesLoading);
    const [openDialog, setOpenDialog] = useState()
    const [operation, setOperation] = useState()
    const [dialogData, setDialogData] = useState(initialDialogData)
    const [dateFilters, setDateFilters] = useState(initialFilters)
    const [sortOptions, setSortOptions] = useState(initialSortOptions)
    const [taxonomyFilter, setTaxonomyFilter] = useState([]);
    const loading = [loadingCampaignTypes, loadingCampaigns, loadingTaxonomies].some(l=> l === "loading" || l === "idle") ? "loading" : "succeeded"

    useEffect(()=>{
        getCampaignsData(mapPageInfo(campaigns))
        dispatch(getCampaignTypes());
        dispatch(getTaxonomyDescriptions());
    },[])

    function mapPageInfo(campaignsData){
        return {
            pageIndex: campaignsData.pageIndex ?? 0,
            pageSize: campaignsData.pageSize ?? 10,
            orderByField: campaignsData.columna ?? "startDate",
            orderByAscending: campaignsData.sortOrderAsc ?? false,
        }
    }
      
    const getCampaignsData = (pageInfo) => {
        let query = `?pageIndex=${pageInfo.pageIndex}&pageNum=${pageInfo.pageSize}`
        if(pageInfo.searchString?.trim().length > 0){
            query = query.concat(`&searchString=${pageInfo.searchString.trim()}`)
        }
        if(pageInfo.orderByField)
            {
                query = query.concat(`&orderByField=${pageInfo.orderByField}&orderByAscending=${pageInfo.orderByAscending}`)
            }
        if(dateFilters.startDate)
            query = query.concat(`&creationDate=${dateFilters.startDate}`)
        if(dateFilters.endDate)
            query = query.concat(`&creationDateUntil=${dateFilters.endDate}`)
        if(taxonomyFilter.length > 0)
        {
            const taxonomyIds = taxonomyFilter.map(t=> Number(t.split(':')[0]))
            query = query.concat(`&caseTaxonomies=${taxonomyIds.join(",")}`)
        }
        dispatch(getCampaigns({query}));
    }

    function renderTaxonomyCell(rowTaxonomies){
        const toDescriptions= rowTaxonomies.map(rt=> transformToTaxonomyDescriptions(rt))

        return(
            <div style={{display: "flex", flexDirection: "column", rowGap: "5px"}}>
                {toDescriptions.map(d=><div key={d}>{d}</div>)}
            </div>
        )
    }

    function transformToTaxonomyDescriptions(taxonomy){
        const descriptionArray = [];

        if(taxonomy.level_1)
            descriptionArray.push(taxonomyDescriptions.find(td=> td.key === taxonomy.level_1).value);
        if(taxonomy.level_2)
            descriptionArray.push(taxonomyDescriptions.find(td=> td.key === taxonomy.level_2).value)
        if(taxonomy.level_3)
            descriptionArray.push(taxonomyDescriptions.find(td=> td.key === taxonomy.level_3).value)
        if(taxonomy.level_4)
            descriptionArray.push(taxonomyDescriptions.find(td=> td.key === taxonomy.level_4).value)
        
        return  `${taxonomy.taxonomyId}: ${descriptionArray.join(", ")}`
    }

    function handleEditDialogOpen({row}){
        const {data} = row;
        const {taxonomies} = data;
        const internalTaxIds = generateMultiple(taxonomies.length, {length: 8});
        const uniqueIdTax = taxonomies.map((tax, index)=> ({...tax, externalId: internalTaxIds[index]}))
        setOperation("edit")
        setDialogData({...data, taxonomies: uniqueIdTax})    
        setOpenDialog(true)
    }

    function handleDeleteDialogOpen({row}){
        const {data} = row;
        setOperation("delete")
        setDialogData(data)    
        setOpenDialog(true)
    }

    function handleCreateDialogOpen(){
        setOperation("create")
        setOpenDialog(true)
    }

    function handleDialogClose(){ 
        setOpenDialog(false)
        setDialogData(initialDialogData)   
        setOperation(null)
    }

    async function createNewCampaign(newCampaign){

        const submitData = {...newCampaign}
        if(!validateCampaignData(submitData))
            return;
        const datesToUtc = {startDate: convertToUTC(newCampaign.startDate), endDate: convertToUTC(newCampaign.endDate)}
        const convertedData = {...submitData, ...datesToUtc}
        const response =  await dispatch(createCampaign({body: convertedData}));
        if(response.payload){
            getCampaignsData(mapPageInfo({...campaigns, ...sortOptions}));
            setOpenDialog(false);
            setDialogData(initialDialogData);
        }
    }

    async function editCampaign(campaignData){

        const submitData = {...campaignData, ...datesToUtc}
        if(!validateCampaignData(submitData))
                return;
        
        const datesToUtc = {startDate: convertToUTC(campaignData.startDate), endDate: convertToUTC(campaignData.endDate)}
        const convertedData = {...submitData, ...datesToUtc}

        const response =  await dispatch(updateCampaign({body: convertedData, param: campaignData.campaignId}));
        if(response.payload){
            getCampaignsData(mapPageInfo({...campaigns, ...sortOptions}));
            setOpenDialog(false);
            setDialogData(initialDialogData);
        }
    }

    async function deleteCampaign(campaignId){
          const response = await dispatch(deactivateCampaign({param: campaignId}))
          if(response.payload){
            getCampaignsData(mapPageInfo({...campaigns, ...sortOptions}));
                setOpenDialog(false);
                setDialogData(initialDialogData);
        }
    }

    const columnsCampaigns = [
        <Column cssClass={"campaign-column"} sortIndex={1} key={"campaigns-1"} width="25%" dataField="campaignName" caption="Nome"/>, 
        <Column cssClass={"campaign-column"}  columnkey={"campaigns-2"} lookup={{allowClearing: false, dataSource: campaignTypes, displayExpr:'description', valueExpr: 'campaignTypeId'}} dataField="campaignTypeId" width="100px" caption="Tipo"></Column>, 
        <Column cssClass={"campaign-column"}  key={"campaigns-4"} dataField="startDate" dataType={"date"} width={100} caption="Data de início"/>,
        <Column cssClass={"campaign-column"}  key={"campaigns-5"} dataField="endDate" dataType={"date"} width={100} caption="Data final"/>,
        <Column key={"campaigns-6"}   cellRender={({value})=>renderTaxonomyCell(value)} width="35%" dataField="taxonomies" caption="Taxonomias"></Column>,
        <Column cssClass={"campaign-column"} dataField={"rejectTaxonomy"} key={"campaigns-7"} cellRender={({value})=>renderTaxonomyCell([value])} width={"15%"} caption="Taxonomia de rejeição"/>,
        <Column key={"acciones"} width={100} type="buttons">
            <Button onClick = { handleEditDialogOpen } hint={ "Editar" } icon = "edit" style={{ marginRight: '10px' }} />
            <Button onClick={handleDeleteDialogOpen} hint={"Eliminar"} icon="trash" />
        </Column>
    ] 

    function onSubmit(data){
        if(operation === "create")
            createNewCampaign(data)
        if(operation === "edit")
            editCampaign(data)
        if(operation === "delete")
            deleteCampaign(data.campaignId)
    }

    function validateCampaignData(submitData){

        if(submitData.campaignName === null || submitData.campaignName?.trim() === "")
            {
                dispatch(openModal({type: "warning", message: "Por favor, preencha o nome."}))
                return false;
            }
        if(!submitData.campaignTypeId)
            {
                dispatch(openModal({type: "warning", message: "Por favor, preencha o tipo"}))
                return false;
            }
        if(submitData.startDate === null)
            {
                dispatch(openModal({type: "warning", message: "Por favor, preencha a data de início"}))
                return false;
            }
        if(submitData.endDate === null)
            {
                dispatch(openModal({type: "warning", message: "Por favor, preencha a data final"}))
                return false;
            }
        
        const rejectTaxonomyIsInCampaignTaxonomies = submitData.taxonomies.find(tax=> 
                                                                                        tax.level_1 === submitData.rejectTaxonomy.level_1 &&
                                                                                        tax.level_2 === submitData.rejectTaxonomy.level_2 &&
                                                                                        tax.level_3 === submitData.rejectTaxonomy.level_3 &&
                                                                                        tax.level_4 === submitData.rejectTaxonomy.level_4
                                                                                    ) 

        if(rejectTaxonomyIsInCampaignTaxonomies)
                {
                    dispatch(openModal({type: "warning", message: "A taxonomia de rejeição não pode estar incluída nas taxonomias de Campanha."}))
                    return false
                }

        return true;
    }

    function handleChangeDateFilter(type, value){
        setDateFilters(prevState=>({...prevState, [type]: value}))
    }

    const customSearchFields = <div style={{display: "flex", alignItems: "center", columnGap: "6px", marginLeft: "6px"}}>
        <DateBox  dateSerializationFormat="yyyy-MM-dd" height={35} value={dateFilters.startDate} placeholder="Fecha Inicio" onValueChanged={(e)=>handleChangeDateFilter("startDate", e.value)}/>
        <DateBox  dateSerializationFormat="yyyy-MM-dd" value={dateFilters.endDate} min={dateFilters.startDate} placeholder="Fecha Fin" onValueChanged={(e)=>handleChangeDateFilter("endDate", e.value)}/>
        <TreeSelector taxonomies={taxonomyFilter} onSelectionChanged={(e)=>setTaxonomyFilter(e)} selectNodesRecursive/>
    </div>

return(
    <>
        <AdminTable
            sectionTitle={"Campanhas"}
            masterTitle={"Campanha"} 
            itemIdField={"campaignId"} 
            itemDescriptionField={"campaignName"} 
            dataSource={campaigns}
            columnasTabla={columnsCampaigns}
            numElements={[10, 20, 30]} 
            getData={getCampaignsData}
            customEdition
            customCreation
            loading={loading}
            allowDelete
            customSearchFields={customSearchFields}
            onCreate={handleCreateDialogOpen}
            defaultSortField={"startDate"}
            sortField={sortOptions}
            setsortField={setSortOptions}
        />
        <CampaignsDialogue onSubmitOperation={onSubmit} operation={operation} handleDialogClose={handleDialogClose} open={openDialog} data={dialogData}/>
    </>

)
}

export {CampaignManagement}